import { useEffect, useRef, useState } from "preact/hooks";
import { clock, MAX_RECORDING_SECONDS, supportedRecordingType } from "./audio.ts";
import { message } from "./errors.ts";
import { Info, StopSquare } from "./icons.tsx";
import type { Recorded } from "./navigation.ts";
import { Button, Callout, ScreenHead, ScreenTitle } from "./ui.tsx";
import { Waveform } from "./waveform.tsx";

interface Props {
  /** What the recording is for, so the screen names it. */
  topic: string;
  onBack: () => void;
  /** Handed the recording once it stops; the review screen saves it. */
  onRecorded: (recording: Recorded) => void;
}

/**
 * Record straight into the page (7.3). The original is what gets uploaded as
 * the master; the API derives the file the phone plays.
 */
export function RecordScreen({ topic, onBack, onRecorded }: Props) {
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [error, setError] = useState<string | null>(null);
  const recorder = useRef<MediaRecorder | null>(null);
  const type = supportedRecordingType();

  useEffect(() => {
    return () => {
      for (const track of recorder.current?.stream.getTracks() ?? []) track.stop();
    };
  }, []);

  useEffect(() => {
    if (!recording) return;
    const started = Date.now();
    const timer = setInterval(() => {
      const value = (Date.now() - started) / 1000;
      setElapsed(value);
      if (value >= MAX_RECORDING_SECONDS) stop();
    }, 250);
    return () => clearInterval(timer);
  }, [recording]);

  async function start(): Promise<void> {
    if (!type) return;
    setError(null);
    try {
      const live = await navigator.mediaDevices.getUserMedia({ audio: true });
      const chunks: Blob[] = [];
      const started = Date.now();
      const active = new MediaRecorder(live, { mimeType: type });
      active.ondataavailable = (event: BlobEvent) => {
        if (event.data.size > 0) chunks.push(event.data);
      };
      active.onstop = () => {
        for (const track of live.getTracks()) track.stop();
        setStream(null);
        setRecording(false);
        const blob = new Blob(chunks, { type });
        onRecorded({
          blob,
          url: URL.createObjectURL(blob),
          contentType: type,
          seconds: (Date.now() - started) / 1000,
        });
      };
      recorder.current = active;
      active.start();
      setStream(live);
      setElapsed(0);
      setRecording(true);
    } catch (reason: unknown) {
      // Nearly always a refused microphone permission.
      setError(message(reason));
      setRecording(false);
    }
  }

  function stop(): void {
    if (recorder.current?.state === "recording") recorder.current.stop();
    else setRecording(false);
  }

  return (
    <>
      <ScreenHead onBack={onBack} />
      <div class="narrow">
        {recording ? (
          <>
            <ScreenTitle centred>
              <span class="muted">Recording…</span>
            </ScreenTitle>
            <p class="lede centred meta-tight">for {topic}</p>
            <p
              class="timer"
              role="timer"
              aria-live="off"
              aria-label={`Recording, ${clock(Math.floor(elapsed / 10) * 10)} so far`}
            >
              {clock(elapsed)}
            </p>
            <Waveform stream={stream} />
            <Button tone="bad" full onClick={stop}>
              <StopSquare />
              <span>Stop</span>
            </Button>
            <p class="meta centred">2 minute maximum</p>
          </>
        ) : (
          <>
            <ScreenTitle centred>Record a message</ScreenTitle>
            <p class="lede centred meta-tight">for {topic}</p>
            {error && (
              <Callout tone="bad" title={error}>
                Allow the microphone in your browser's address bar and try again.
              </Callout>
            )}
            <Button tone="primary" full onClick={() => void start()}>
              Start recording
            </Button>
            <p class="meta centred">2 minute maximum</p>
          </>
        )}
        <p class="footnote">
          <Info />
          <span>Your browser may ask to use your microphone.</span>
        </p>
      </div>
    </>
  );
}
